// Import types for documentation purposes
// import { CareDaily, TrendsMetadata, Trends } from '../types';
import { CorsService } from './corsService';
import { getMockWellnessData, getMockTimeSeriesData, isDemoMode } from './demoDataService';
import { JWTService } from './jwtService';

// Wellness assessments can be years apart, so time-series fetches (trends,
// wellness_assessment) look back 5 years to capture enough points for charts.
const TIME_SERIES_LOOKBACK_MS = 5 * 365 * 24 * 60 * 60 * 1000;

export interface CareDailyConfig {
  apiKey: string;
  baseUrl: string;
  useSandbox?: boolean;
  forceProxy?: boolean;
  /**
   * Route through the Netlify proxy and authenticate with the GoIcon token
   * instead of an API key. The proxy resolves the facility's CareDaily
   * credentials server-side, so the key never exists in the browser.
   */
  useProxyAuth?: boolean;
}

export class CareDailyService {
  private config: CareDailyConfig;

  constructor(config: CareDailyConfig) {
    this.config = config;
  }

  /**
   * Get the appropriate base URL based on environment
   */
  private getBaseUrl(): string {
    // Proxy-authenticated requests must always go through the function, which
    // is the only place the CareDaily key exists.
    if (this.config.forceProxy || this.config.useProxyAuth) {
      // Prefer explicit functions base when provided
      const explicitBase = process.env.REACT_APP_FUNCTIONS_BASE; // e.g. https://wellness.goicon.co/.netlify/functions or http://localhost:8888/.netlify/functions
      if (explicitBase) {
        return `${explicitBase.replace(/\/$/, '')}/caredaily-proxy`;
      }
      // If running via netlify dev (common default port), relative path works
      if (window.location.port === '8888') {
        return '/.netlify/functions/caredaily-proxy';
      }
      // In production hosts, relative path works
      if (window.location.hostname.includes('netlify.app') || window.location.hostname.includes('goicon.co')) {
        return '/.netlify/functions/caredaily-proxy';
      }
      // Local dev fallback: hit deployed functions to avoid HTML from app dev server
      return 'https://wellness.goicon.co/.netlify/functions/caredaily-proxy';
    }
    // Check if we have a proxy server configured for production
    const proxyUrl = process.env.REACT_APP_PROXY_URL;
    if (proxyUrl && proxyUrl !== 'https://wellness.goicon.co') {
      console.log('🔄 Using external proxy server:', proxyUrl);
      return `${proxyUrl}/api/caredaily`;
    }
    
    // In development, use proxy to avoid CORS issues
    if (process.env.NODE_ENV === 'development') {
      return '/cloud/json'; // This will be proxied to the CareDaily API
    }
    
    // In production on Netlify, use Netlify Functions as proxy
    if (window.location.hostname.includes('netlify.app') || window.location.hostname.includes('goicon.co')) {
      return '/.netlify/functions/caredaily-proxy';
    }
    
    // Fallback: use full URLs directly (may have CORS issues)
    if (process.env.NODE_ENV !== 'production') {
      console.log('⚠️ Using direct CareDaily API (may have CORS issues)');
    }
    if (this.config.useSandbox) {
      return process.env.REACT_APP_CAREDAILY_SANDBOX_URL || 'https://sboxall.peoplepowerco.com/cloud/json';
    }
    return process.env.REACT_APP_CAREDAILY_PRODUCTION_URL || 'https://app.caredaily.ai/cloud/json';
  }



  /** Milliseconds before a single CareDaily API attempt is aborted */
  private static readonly REQUEST_TIMEOUT_MS = 20_000;
  /** Total number of attempts (1 initial + N-1 retries) */
  private static readonly MAX_ATTEMPTS = 3;
  /** Base delay in ms for exponential backoff between retries */
  private static readonly RETRY_BASE_DELAY_MS = 1_000;

  /**
   * Returns true for errors that are worth retrying (transient),
   * false for permanent errors (auth, not-found, business logic).
   */
  private static isRetryable(error: unknown): boolean {
    if (!(error instanceof Error)) return false;
    // Timeout
    if (error.name === 'AbortError') return true;
    // Network-level failure (no response at all)
    if (error.message.toLowerCase().includes('failed to fetch')) return true;
    // 5xx surfaced as messages by our handler below
    if (
      error.message.includes('server error') ||
      error.message.includes('temporarily unavailable') ||
      error.message.includes('Function returned HTML')
    ) return true;
    return false;
  }

  /**
   * Single attempt at a fetch — no retry logic here.
   * Throws on any failure; caller decides whether to retry.
   */
  private async attemptRequest<T>(url: URL, method: string, params?: Record<string, any>): Promise<T> {
    const controller = new AbortController();
    const timeoutId = setTimeout(
      () => controller.abort(),
      CareDailyService.REQUEST_TIMEOUT_MS
    );

    try {
      const requestHeaders: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (this.config.useProxyAuth) {
        // The proxy authorizes on this token and supplies the CareDaily key.
        const token = JWTService.getToken();
        if (!token) {
          throw new Error('Not authenticated - no GoIcon token available for CareDaily request');
        }
        requestHeaders['Authorization'] = `Bearer ${token}`;
      } else if (this.config.apiKey) {
        requestHeaders['API_KEY'] = this.config.apiKey;
      }

      const fetchOptions: RequestInit = {
        method,
        headers: requestHeaders,
        signal: controller.signal,
      };

      if ((method === 'POST' || method === 'PUT') && params) {
        fetchOptions.body = JSON.stringify(params);
      }

      const response = await CorsService.corsEnabledFetch(url.toString(), fetchOptions);

      if (process.env.NODE_ENV !== 'production') {
        console.log(`📡 Response status: ${response.status} ${response.statusText}`);
        console.log(`📡 Response headers:`, Object.fromEntries(response.headers.entries()));
      }

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ HTTP error response:`, errorText);

        let userFriendlyError = `API request failed with status ${response.status}`;
        switch (response.status) {
          case 401:
            userFriendlyError = 'Authentication failed - Invalid or missing API key';
            break;
          case 403:
            userFriendlyError = 'Access denied - Check API permissions for this resident/location';
            break;
          case 404: {
            const locationId = url.pathname.includes('locations/') ? url.pathname.split('/').find((_s, i, arr) => arr[i - 1] === 'locations') ?? 'unknown' : 'unknown';
            userFriendlyError = `Resident/Location ID "${locationId}" not found in CareDaily system`;
            break;
          }
          case 429:
            userFriendlyError = 'Rate limit exceeded - Too many API requests';
            break;
          case 500:
            userFriendlyError = 'CareDaily server error - Please try again later';
            break;
          case 502:
          case 503:
          case 504:
            userFriendlyError = 'CareDaily service temporarily unavailable';
            break;
          default:
            userFriendlyError = `API error (${response.status}): ${errorText}`;
        }
        throw new Error(userFriendlyError);
      }

      const responseText = await response.text();

      if (responseText.startsWith('<!DOCTYPE') || responseText.includes('<html>')) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('❌ Received HTML instead of JSON');
          console.error('📄 HTML response (first 300 chars):', responseText.substring(0, 300));
        }
        throw new Error('Function returned HTML error page instead of JSON data');
      }

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('❌ Failed to parse response as JSON:', parseError);
          console.error('❌ Response text:', responseText);
        }
        const errorMessage = parseError instanceof Error ? parseError.message : 'Unknown parsing error';
        throw new Error(`Invalid JSON response: ${errorMessage}`);
      }

      if (process.env.NODE_ENV !== 'production') {
        console.log(`✅ API response data:`, data);
      }

      if (data.resultCode !== undefined && data.resultCode !== 0) {
        if (data.resultCode === 7) {
          throw new Error(`ACCESS_DENIED: Location not accessible with current credentials`);
        }
        throw new Error(`API error: ${data.resultCodeMessage || 'Unknown error'}`);
      }

      return data;
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        console.error(`⏱️ CareDaily request timed out after ${CareDailyService.REQUEST_TIMEOUT_MS / 1000}s`);
        throw new Error(`Request timed out. The wellness data service is taking too long to respond. Please try again.`);
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * Generic API request handler with automatic retry on transient failures.
   * Public to allow use by CareDailyGoalsService.
   */
  public async makeRequest<T>(
    endpoint: string,
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET',
    params?: Record<string, any>
  ): Promise<T> {
    const baseUrl = this.getBaseUrl();

    let url: URL;
    if (baseUrl.startsWith('/.netlify/functions/') || baseUrl.startsWith('/')) {
      url = new URL(`${baseUrl}${endpoint}`, window.location.origin);
    } else {
      url = new URL(`${baseUrl}${endpoint}`);
    }

    if ((method === 'GET' || method === 'DELETE') && params) {
      Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach(v => url.searchParams.append(key, v));
        } else {
          url.searchParams.append(key, value);
        }
      });
    }

    if (baseUrl.startsWith('/.netlify/functions/')) {
      url.searchParams.set('env', this.config.useSandbox ? 'sandbox' : 'production');
    }

    if (process.env.NODE_ENV !== 'production') {
      console.log(`🌐 Making ${method} request to: ${url.toString()}`);
      if (!baseUrl.startsWith('/.netlify/functions/')) {
        console.log(`🔑 Using API key: ${!!this.config.apiKey}`);
      } else {
        console.log('🔐 Using server-side API key via Netlify function');
      }
    }

    let lastError: unknown;
    for (let attempt = 1; attempt <= CareDailyService.MAX_ATTEMPTS; attempt++) {
      try {
        return await this.attemptRequest<T>(url, method, params);
      } catch (error) {
        lastError = error;
        const isLastAttempt = attempt === CareDailyService.MAX_ATTEMPTS;

        if (isLastAttempt || !CareDailyService.isRetryable(error)) {
          break;
        }

        const delayMs = CareDailyService.RETRY_BASE_DELAY_MS * Math.pow(2, attempt - 1);
        console.warn(`⚠️ Attempt ${attempt} failed for ${endpoint} — retrying in ${delayMs}ms…`, error instanceof Error ? error.message : error);
        await new Promise(resolve => setTimeout(resolve, delayMs));
      }
    }

    console.error(`❌ CareDaily API request failed after ${CareDailyService.MAX_ATTEMPTS} attempts for ${endpoint}:`, lastError);
    throw lastError;
  }

  /**
   * Get CareDaily user and location by external user ID using the NEW API method
   * This uses the improved /cloud/json/user endpoint with externalUserId, externalAppId, and externalRole parameters
   * 
   * @param externalUserId - The external user ID from the third-party system (e.g., GoIcon v_external_id)
   * @param externalAppId - The external application ID (e.g., 1001 for Moorings Park)
   * @param externalRole - The external user role: 0 = resident, 1 = related party, 2 = SSO (defaults to 0)
   * @returns User object with locationId for wellness data queries
   */
  async getUserByExternalId(
    externalUserId: string,
    externalAppId?: number,
    externalRole: number = 0
  ): Promise<{
    userId: string;
    locationId: string;
    firstName?: string;
    lastName?: string;
  }> {
    try {
      console.log('🔍 Looking up CareDaily user by external user ID:', { externalUserId, externalAppId, externalRole });
      
      // If externalAppId is provided, use the new API method
      if (externalAppId !== undefined && externalAppId !== null) {
        console.log('📍 Using NEW API method with externalAppId:', externalAppId);
        
        const userEndpoint = `/user`;
        const userParams = {
          externalUserId: externalUserId,
          externalAppId: externalAppId,
          externalRole: externalRole
        };
        
        console.log('📍 Calling /user endpoint with params:', userParams);
        
        const userData: any = await this.makeRequest(userEndpoint, 'GET', userParams);
        
        if (!userData.user) {
          throw new Error(`No CareDaily user found with external ID: ${externalUserId}`);
        }
        
        const user = userData.user;
        console.log('✅ Found user:', {
          userId: user.id,
          name: `${user.firstName || ''} ${user.lastName || ''}`,
          email: user.email
        });
        
        // Find the care recipient location (subType=0 and locationUser.role=1)
        if (!userData.locations || userData.locations.length === 0) {
          throw new Error(`No locations found for user with external ID: ${externalUserId}`);
        }
        
        console.log(`📍 Found ${userData.locations.length} location(s), filtering for care recipient...`);
        
        // Find the residential location where user is the care recipient
        // Note: API returns locationUser (singular) with role property, not locationUsers (plural) array
        const careRecipientLocation = userData.locations.find((loc: any) => {
          const isResidentialLocation = loc.subType === 0;
          const isCareRecipient = loc.locationUser?.role === 1;
          
          if (process.env.NODE_ENV !== 'production') {
            console.log(`🔍 Evaluating location ${loc.id} (${loc.name}):`, {
              subType: loc.subType,
              isResidentialLocation,
              locationUser: loc.locationUser,
              isCareRecipient
            });
          }
          
          return isResidentialLocation && isCareRecipient;
        });
        
        if (!careRecipientLocation) {
          console.error('❌ No care recipient location found. Available locations:', userData.locations);
          throw new Error(`No care recipient location (subType=0, role=1) found for external ID: ${externalUserId}`);
        }
        
        console.log('✅ Found care recipient location:', {
          locationId: careRecipientLocation.id,
          name: careRecipientLocation.name
        });
        
        return {
          userId: user.id?.toString(),
          locationId: careRecipientLocation.id?.toString(),
          firstName: user.firstName,
          lastName: user.lastName
        };
      }
      
      // FALLBACK: Use LEGACY API method (old approach with /admin/json/users)
      console.log('⚠️ No externalAppId provided, falling back to LEGACY API method');
      
      // Step 1: Look up user by externalUserId
      const userEndpoint = `/admin/json/users`;
      const userParams = { externalUserId: externalUserId };
      
      console.log('📍 Step 1: Looking up user with params:', userParams);
      
      const userData: any = await this.makeRequest(userEndpoint, 'GET', userParams);
      
      if (!userData.users || userData.users.length === 0) {
        throw new Error(`No CareDaily user found with external ID: ${externalUserId}`);
      }
      
      const user = userData.users[0];
      const userName = `${user.firstName} ${user.lastName}`;
      const userCreationMs = user.creationDateMs;
      
      console.log('✅ Found user:', {
        userId: user.id,
        name: userName,
        creationMs: userCreationMs
      });
      
      // Step 2: Get all accessible locations
      console.log('📍 Step 2: Fetching locations to find matching location ID...');
      const locationsEndpoint = `/admin/json/locations`;
      const locationsData: any = await this.makeRequest(locationsEndpoint, 'GET', {});
      
      if (!locationsData.locations || locationsData.locations.length === 0) {
        throw new Error(`No accessible locations found`);
      }
      
      // Step 3: Find location with matching name and creation date
      // The user and location are created at the same time with the same name
      const matchingLocation = locationsData.locations.find((loc: any) => 
        loc.name === userName && loc.creationDateMs === userCreationMs
      );
      
      if (!matchingLocation) {
        console.error('❌ No matching location found for user:', {
          userName,
          userCreationMs,
          searchedLocations: locationsData.locations.length
        });
        throw new Error(`No location found matching user: ${userName} (external ID: ${externalUserId})`);
      }
      
      console.log('✅ Found matching location:', {
        locationId: matchingLocation.id,
        name: matchingLocation.name,
        organizationId: matchingLocation.organizationId
      });
      
      return {
        userId: user.id?.toString(),
        locationId: matchingLocation.id?.toString(),
        firstName: user.firstName,
        lastName: user.lastName
      };
      
    } catch (error) {
      console.error('❌ Failed to lookup user by external ID:', error);
      throw error;
    }
  }

  /**
   * DEPRECATED: This method is no longer needed.
   * Use getUserByExternalId() instead, which now returns locationId.
   * 
   * @deprecated Use getUserByExternalId() instead
   */
  async getLocationIdByExternalId(externalUserId: string): Promise<string> {
    console.warn('⚠️ getLocationIdByExternalId is deprecated - Use getUserByExternalId() instead');
    const user = await this.getUserByExternalId(externalUserId);
    return user.locationId;
  }

  /**
   * Get location state data for wellness trends
   */
  async getLocationState(locationId: string, trendNames?: string[]): Promise<any> {
    // Check if demo mode is enabled
    if (isDemoMode()) {
      console.log('🎭 Demo Mode: Returning mock wellness data');
      return getMockWellnessData();
    }

    const defaultTrendNames = [
      'trends_category',
      'trends_metadata',
      'trends',
      'trends_recently',
      'trends_weekly', 
      'trends_highlights'
    ];

    const names = trendNames || defaultTrendNames;
    const params = names.length > 1 
      ? { name: names } // Multiple names as array
      : { name: names[0] }; // Single name as string

    try {
      const response = await this.makeRequest(`/locations/${locationId}/state`, 'GET', params);
      return response;
    } catch (error) {
      console.error(`❌ CareDaily API request failed for /locations/${locationId}/state:`, error);
      throw error;
    }
  }

  /**
   * Get time-series wellness data
   */
  async getTimeSeriesData(
    locationId: string, 
    startDate?: number, 
    endDate?: number,
    trendName: string = 'trends'
  ): Promise<any> {
    // Check if demo mode is enabled
    if (isDemoMode()) {
      console.log('🎭 Demo Mode: Returning mock time series data');
      const days = Math.ceil((endDate || Date.now() - (startDate || Date.now() - (365 * 24 * 60 * 60 * 1000))) / (24 * 60 * 60 * 1000));
      return getMockTimeSeriesData(Math.min(days, 365));
    }

    const now = new Date();
    const defaultEndDate = endDate || now.getTime();
    const defaultStartDate = startDate || (now.getTime() - TIME_SERIES_LOOKBACK_MS);

    const params = {
      name: trendName,
      startDate: defaultStartDate,
      endDate: defaultEndDate
    };

    try {
      const response = await this.makeRequest(`/locations/${locationId}/timeStates`, 'GET', params);
      return response;
    } catch (error) {
      console.error(`❌ CareDaily API request failed for /locations/${locationId}/timeStates:`, error);
      throw error;
    }
  }

  /**
   * Fetch multiple time-series in parallel (e.g., trends and wellness_assessment)
   */
  async getCombinedTimeSeries(
    locationId: string,
    startDate?: number,
    endDate?: number,
    trendNames: string[] = ['trends', 'wellness_assessment']
  ): Promise<any> {
    // Check if demo mode is enabled
    if (isDemoMode()) {
      console.log('🎭 Demo Mode: Returning mock combined time series data');
      const days = Math.ceil((endDate || Date.now() - (startDate || Date.now() - (365 * 24 * 60 * 60 * 1000))) / (24 * 60 * 60 * 1000));
      return { states: getMockTimeSeriesData(Math.min(days, 365)) };
    }

    const now = new Date();
    const defaultEndDate = endDate || now.getTime();
    const defaultStartDate = startDate || (now.getTime() - TIME_SERIES_LOOKBACK_MS);

    const makeParams = (name: string) => ({
      name,
      startDate: defaultStartDate,
      endDate: defaultEndDate
    });

    const requests = trendNames.map(name =>
      this.makeRequest(`/locations/${locationId}/timeStates`, 'GET', makeParams(name))
        .then(resp => ({ name, resp }))
        .catch(err => ({ name, err }))
    );

    const results = await Promise.all(requests);
    // Merge states arrays, tagging by trend name if needed
    const merged: any = { states: [] };
    results.forEach(r => {
      if ((r as any).resp?.states) {
        merged.states.push(...(r as any).resp.states);
      }
    });
    return merged;
  }

  /**
   * Get current-state wellness data for a resident by resident ID.
   * Returns the latest snapshot only — use getCombinedTimeSeries for historical chart data.
   * Note: In CareDaily API, resident ID = location ID
   */
  async getResidentWellnessData(residentId: string): Promise<{ currentState: any }> {
    if (process.env.NODE_ENV !== 'production') {
      console.log('📍 Using resident ID as location ID:', residentId);
    }
    return this.getResidentWellnessDataByLocation(residentId);
  }

  /**
   * Get current-state wellness data for a location.
   * Fetches /locations/{id}/state only — the latest snapshot of all trend states.
   * Historical data for charts is fetched separately via getCombinedTimeSeries.
   */
  async getResidentWellnessDataByLocation(locationId: string): Promise<{ currentState: any }> {
    try {
      const currentState = await this.getLocationState(locationId);
      return { currentState };
    } catch (error) {
      console.error('Failed to fetch wellness data for location:', error);
      throw error;
    }
  }

  /**
   * Parse and structure wellness data for dashboard consumption
   */
  parseWellnessData(rawData: any): {
    metadata: Record<string, any>;
    currentTrends: Record<string, any>;
    recentTrends?: Record<string, any>;
    weeklyTrends?: Record<string, any>;
    highlights?: Record<string, any>;
  } {
    const states = rawData.currentState?.states || [];
    const result: any = {
      metadata: {},
      currentTrends: {},
      recentTrends: {},
      weeklyTrends: {},
      highlights: {}
    };

    states.forEach((state: any) => {
      switch (state.name) {
        case 'trends_metadata':
          result.metadata = state.value || {};
          break;
        case 'trends':
          result.currentTrends = state.value || {};
          break;
        case 'trends_recently':
          result.recentTrends = state.value || {};
          break;
        case 'trends_weekly':
          result.weeklyTrends = state.value || {};
          break;
        case 'trends_highlights':
          result.highlights = state.value || {};
          break;
      }
    });

    return result;
  }

  /**
   * Walk the raw /timeStates array and return the most recent full trend object
   * (value/avg/std/zscore/display) for every metric key found.
   *
   * This is the best source for DOW metrics: trends_recently only has avg/std/n
   * (rolling window, no display or zscore), whereas the time series has the
   * actual observed value on each day.
   */
  extractLatestFromTimeSeries(rawTimeSeriesData: any): Record<string, any> {
    const latest: Record<string, { data: any; dateMs: number }> = {};
    const states: any[] = rawTimeSeriesData?.states ?? [];

    for (const state of states) {
      if (
        (state.name === 'trends' || state.name === 'wellness_assessment') &&
        state.value &&
        typeof state.stateDateMs === 'number'
      ) {
        for (const [key, data] of Object.entries(state.value)) {
          if (!data || typeof data !== 'object') continue;
          if (!latest[key] || state.stateDateMs > latest[key].dateMs) {
            latest[key] = { data, dateMs: state.stateDateMs };
          }
        }
      }
    }

    return Object.fromEntries(Object.entries(latest).map(([k, v]) => [k, v.data]));
  }

  /**
   * Map CareDaily metrics to dashboard format.
   *
   * Priority for current value displayed on cards:
   *   1. trends_highlights.now  — richest data for trend.* metrics (value + zscore)
   *   2. latestFromTimeSeries   — actual observed value for DOW and other metrics
   *   3. trends_recently        — rolling window avg/std/n (last resort)
   *
   * Each metric is stored under:
   *   - its original CareDaily API key  (e.g. "trend.wellness_score")
   *   - a camelCase alias               (e.g. "wellnessScore")  ← backward compat
   *
   * NOTE: getMetricDataByKey in Dashboard.tsx always tries _rawData.recentTrends
   * first so configured careDailyValueKey / careDailyDisplayKey sub-key overrides
   * are applied before these pre-mapped defaults are ever consulted.
   */
  mapMetricsForDashboard(wellnessData: any, latestFromTimeSeries?: Record<string, any>): Record<string, any> {
    const { metadata, recentTrends, highlights } = wellnessData;
    const mappedMetrics: Record<string, any> = {};

    if (process.env.NODE_ENV !== 'production') {
      console.log('🔍 Mapping wellness data:', { metadata, recentTrends, highlights });
    }

    // trends_highlights.now has actual point-in-time value + zscore for trend.* metrics.
    const highlightsNow: Record<string, any> = highlights?.now ?? {};
    const tsLatest: Record<string, any> = latestFromTimeSeries ?? {};

    // Collect all metric keys from all sources
    const allKeys = new Set<string>([
      ...Object.keys(recentTrends ?? {}),
      ...Object.keys(highlightsNow),
      ...Object.keys(tsLatest),
    ]);

    // The verdict that belongs on `_rawData.recentTrends[key]` once the sources below
    // are merged field-by-field - stamped from `valueSource`, the one object that
    // actually won the value for this key, computed once here while we still know
    // which source that was. The merge itself (further down) combines fields from
    // multiple source objects, so by the time it is done there is no way to tell
    // which source wrote which field - asking a downstream reader (extractTrendFields)
    // to infer that is exactly what let a newer value keep an older source's verdict.
    const verdictByKey: Record<string, { interpretation?: string; basis?: string }> = {};

    allKeys.forEach(key => {
      const td = recentTrends?.[key];  // rolling avg only (avg/std/n)
      const hn = highlightsNow[key];   // trend.* current value + zscore
      const ts = tsLatest[key];        // most recent time series entry (value/display/zscore)

      // Skip if there's no actual data object anywhere
      if (!td && !hn && !ts) return;

      // Mirror the value fallback chain below so we know exactly which source
      // object supplied `value`. CareDaily's normative_interpretation/basis must
      // come from that same object - a resident must never see a value from one
      // day paired with a comparison computed for another.
      const valueSource = hn?.value != null ? hn
        : ts?.value != null ? ts
        : ts?.avg != null ? ts
        : td?.avg != null ? td
        : td;

      const entry = {
        // Priority: highlights.now → time series latest → trends_recently rolling avg
        value: hn?.value ?? ts?.value ?? ts?.avg ?? td?.avg ?? td?.value,
        display: hn?.display ?? ts?.display ?? td?.display ?? hn?.avg ?? ts?.avg ?? td?.avg ?? td?.value,
        zscore: hn?.zscore ?? ts?.zscore ?? td?.zscore,
        metadata: metadata?.[key] ?? null,
        // CareDaily computes the normative comparison itself, per resident, and
        // publishes it beside the value. Prefer their answer over recomputing
        // from the bands - it is their maths on their data. Present only on the
        // day an assessment was recorded, so it is often absent.
        normativeInterpretation: valueSource?.normative_interpretation,
        normativeBasis: valueSource?.normative_basis,
      };

      verdictByKey[key] = {
        interpretation: valueSource?.normative_interpretation,
        basis: valueSource?.normative_basis,
      };

      // Store under original API key
      mappedMetrics[key] = entry;

      // Also store a camelCase alias for backward compat with legacy metric config keys
      const camelKey = key.replace('trend.', '').replace(/_([a-z])/g, (_: string, l: string) => l.toUpperCase());
      if (camelKey !== key) {
        mappedMetrics[camelKey] = entry;
      }
    });

    // Raw data stored last so it is always available for getMetricDataByKey's primary lookup path.
    // Merge all sources into recentTrends so extractTrendFields also sees the richer fields.
    const mergedRecentTrends = { ...(recentTrends ?? {}) };
    // Overlay time series latest first (lower priority), then highlights.now (higher priority)
    Object.keys(tsLatest).forEach(key => {
      mergedRecentTrends[key] = { ...(mergedRecentTrends[key] ?? {}), ...tsLatest[key] };
    });
    Object.keys(highlightsNow).forEach(key => {
      mergedRecentTrends[key] = { ...(mergedRecentTrends[key] ?? {}), ...highlightsNow[key] };
    });
    // The field-by-field merge above can leave an EARLIER source's
    // normative_interpretation/normative_basis sitting on the object even after a LATER
    // source overwrote `value` with no verdict of its own - the merged bag has no memory
    // of which source wrote which field. Re-stamp the verdict from the same valueSource
    // this function already computed per key, so it always belongs to the value sitting
    // beside it: present it when there is one, delete both keys outright when there isn't
    // (never leave a stale one behind).
    allKeys.forEach(key => {
      const merged = mergedRecentTrends[key];
      if (!merged) return;
      const verdict = verdictByKey[key];
      if (verdict?.interpretation) {
        // Always a NEW object, never a mutation - for a td-only key,
        // `mergedRecentTrends[key]` is still the SAME reference as the original
        // `recentTrends[key]`, and mutating it would corrupt `rawRecentTrends`
        // (the unmerged copy kept for the pie chart's pure rolling averages).
        mergedRecentTrends[key] = { ...merged, normative_interpretation: verdict.interpretation, normative_basis: verdict.basis };
      } else if ('normative_interpretation' in merged || 'normative_basis' in merged) {
        const { normative_interpretation, normative_basis, ...rest } = merged;
        mergedRecentTrends[key] = rest;
      }
    });
    // Keep the original (unmerged) trends_recently as the pie chart's fallback when
    // the trends time series is unavailable (Dashboard adds `wheelTrends` from it),
    // separate from the richer merged data used by metric cards.
    mappedMetrics._rawData = { metadata, recentTrends: mergedRecentTrends, rawRecentTrends: recentTrends ?? {}, highlights };

    if (process.env.NODE_ENV !== 'production') {
      console.log('🎯 Final mapped metrics:', Object.keys(mappedMetrics).filter(k => k !== '_rawData').length, 'keys');
    }
    return mappedMetrics;
  }

  // Note: getFacilityResidents moved to goiconApiService.ts
  // This endpoint is on api.goicon.com, not CareDaily API

  /**
   * Parse time-series data from CareDaily API.
   * @param valueKeyByMetric Optional map of metricKey → careDailyValueKey so each metric
   *   uses its configured sub-field (e.g. 'avg', 'value') rather than the default fallback.
   */
  parseTimeSeriesData(rawTimeSeriesData: any, valueKeyByMetric?: Record<string, string>): Record<string, any[]> {
    console.log('🔍 Raw time-series response:', rawTimeSeriesData);
    const timeSeriesData: Record<string, any[]> = {};
    
    if (rawTimeSeriesData?.states && Array.isArray(rawTimeSeriesData.states)) {
      console.log(`📊 Found ${rawTimeSeriesData.states.length} time-series states`);
      
      // Each state represents a day with trend data
      rawTimeSeriesData.states.forEach((state: any, index: number) => {
        if (process.env.NODE_ENV !== 'production') {
          console.log(`📈 State ${index}:`, { 
            name: state.name, 
            stateDate: state.stateDate, 
            hasValue: !!state.value, 
            valueKeys: state.value ? Object.keys(state.value).length : 0 
          });
        }
        
        if ((state.name === 'trends' || state.name === 'wellness_assessment') && state.value && state.stateDate) {
          // For each metric in this day's data
          Object.keys(state.value).forEach(metricKey => {
            const metricData = state.value[metricKey];

            // Derive a numeric value when possible. Trends often provide objects (value/avg),
            // while wellness_assessment often provides primitives (number or numeric string).
            // Respect per-metric careDailyValueKey config when provided; otherwise use the
            // documented default (avg ?? value). This MUST match extractTrendFields in
            // Dashboard.tsx — if the two disagree, the card's headline number and the chart
            // point for the same day are drawn from different fields.
            let numericValue: number | null = null;
            if (metricData !== null && typeof metricData === 'object') {
              const configuredKey = valueKeyByMetric?.[metricKey];
              const candidate: any = configuredKey
                ? (metricData as any)[configuredKey]
                : ((metricData as any).avg ?? (metricData as any).value);
              if (typeof candidate === 'number') numericValue = candidate;
              else if (typeof candidate === 'string') {
                const parsed = parseFloat(candidate);
                if (Number.isFinite(parsed)) numericValue = parsed;
              }
            } else {
              if (typeof metricData === 'number') numericValue = metricData;
              else if (typeof metricData === 'string') {
                const parsed = parseFloat(metricData);
                if (Number.isFinite(parsed)) numericValue = parsed;
              }
            }

            // Skip non-numeric fields (e.g., Rank labels, names)
            if (numericValue === null) return;

            // Initialize array for this metric if not exists
            if (!timeSeriesData[metricKey]) {
              timeSeriesData[metricKey] = [];
            }

            // Add this day's data point
            timeSeriesData[metricKey].push({
              date: state.stateDate,
              value: numericValue
            });
          });
        }
      });
      
      // Sort by date, then collapse duplicates. The same metric key can appear in
      // both the 'trends' and 'wellness_assessment' feeds on the same day (the
      // admin metric picker already guards against this collision); without the
      // collapse the chart would render two bars/points for one day. Last value
      // for a given calendar day wins.
      Object.keys(timeSeriesData).forEach(metricKey => {
        const byDate = new Map<string, { date: string; value: number }>();
        for (const point of timeSeriesData[metricKey]) {
          byDate.set(point.date, point);
        }
        timeSeriesData[metricKey] = Array.from(byDate.values())
          .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
        console.log(`✅ Processed ${metricKey}: ${timeSeriesData[metricKey].length} data points`);
      });
    } else {
      console.warn('⚠️ No time-series states found in response');
    }
    
    if (process.env.NODE_ENV !== 'production') {
      console.log('📈 Final parsed time-series data:', Object.keys(timeSeriesData).length, 'metrics');
    }
    return timeSeriesData;
  }
}

/**
 * Create a service instance for a facility.
 *
 * The CareDaily API key is deliberately NOT fetched here. Requests go through
 * the Netlify proxy, which resolves the facility's credentials server-side from
 * the caller's verified token and injects the key itself. Keeping the key out
 * of the browser is the point — it used to be decrypted client-side, which
 * meant anyone with the public anon key could retrieve it.
 */
export const createCareDailyService = async (facilityId?: string): Promise<CareDailyService> => {
  if (!facilityId) {
    throw new Error('CareDaily requests require a facilityId.');
  }

  return new CareDailyService({
    // Resolved by netlify/functions/caredaily-proxy from the caller's token.
    apiKey: '',
    baseUrl: '',
    useSandbox: false,
    useProxyAuth: true,
  });
};

// Demo-only helper for parsing mock data without API calls
export const createCareDailyServiceForDemo = (): CareDailyService => {
  const config: CareDailyConfig = {
    apiKey: 'demo',
    baseUrl: 'https://app.caredaily.ai/cloud/json',
    useSandbox: false
  };

  return new CareDailyService(config);
};
